import { Container } from '@cleartrip/ct-design-components'
import React from 'react'

import AirportChangeNotification from '@/components/srp/FlightDetails/FlightItinerary/AirportChangeNotification'
import ArrivalDepartInfo from '@/components/srp/FlightDetails/FlightItinerary/ArrivalDepartInfo'
import BaggageInfo from '@/components/srp/FlightDetails/FlightItinerary/BaggageInfo'
import FlightRouteInfo from '@/components/srp/FlightDetails/FlightItinerary/FlightRouteInfo'
import Layover from '@/components/srp/FlightDetails/FlightItinerary/Layover'
import { Benefit, MetaDataType, searchFlightsType } from '@/types/srp'
import { isEmpty } from '@/utils/general/browserHelper'
import { getChangeInfo, getTransitVisaRequired } from '@/utils/srp/flightDetails'

interface FlightItineraryProps {
    segment: searchFlightsType
    metaData: MetaDataType
    segmentsList: searchFlightsType[]
    index: number
    baggageData: Partial<Benefit>
    baggageAllowancesInfo?: {
        benefitList?: any[]
        benefitSummary?: any[]
    }
    isIntl?: boolean
    searchQuery?: any
    journeyIndex?: number,
    layoverInfo?: any
}

const FlightItinerary = ({ segment, metaData, segmentsList, index, baggageData = {}, baggageAllowancesInfo = {}, isIntl = false, searchQuery = {}, journeyIndex = 0, layoverInfo = {} }: FlightItineraryProps) => {
    const { airportDetail = {}, airlineDetail = {} } = metaData || {}

    const getSegmentsWithTechnicalLayover = (segment: any) => {
      
        try {
            const { stops, arrival = {}, departure = {}, fltNo = '', airlineCode, stopFlightDurations = [] } =
                segment || {}
            if (!Array.isArray(stops) || stops.length === 0) {
                return [segment]
            }

            const getLegDuration = (legIndex: number) => {
                const entry = stopFlightDurations?.[legIndex]
                const hh = Number(entry?.hh) || 0
                const mm = Number(entry?.mm) || 0
                return { hh, mm, totalMinutes: hh * 60 + mm }
            }

            const segmentsWithTechnicalLayover = stops.map((stopData, index) => {
                const prevStopData = stops[index - 1] || {}
                const departureData =
                    index === 0
                        ? departure
                        : {
                              airport: {
                                  code: prevStopData.airportCode,
                                  time: prevStopData.depTime
                              }
                          }
                const { airportCode, arrTime } = stopData
                const currentStopArrival = {
                    airport: {
                        code: airportCode,
                        time: arrTime
                    }
                }
                
                return {
                    airlineCode,
                    fltNo,
                    departure: departureData,
                    arrival: currentStopArrival,
                    duration: getLegDuration(index)
                }
            })
            const lastStopData = stops[stops.length - 1]
            const lastStopDeparture = {
                airport: {
                    code: lastStopData.airportCode,
                    time: lastStopData.depTime
                }
            }
            segmentsWithTechnicalLayover.push({
                airlineCode,
                fltNo,
                departure: lastStopDeparture,
                arrival,
                duration: getLegDuration(stops.length)
            })
            return segmentsWithTechnicalLayover
        } catch (e) {
            console.log(e)
            return [segment]
        }
    }

    const segmentsWithTechnicalLayover: any = getSegmentsWithTechnicalLayover(segment)
    const { arrival = {}, operatingAirlineCode= '', airlineCode= '' } = segment
    const { airport: { code = '' } = {} } = arrival || {}
    const isTripEnd = index === segmentsList?.length - 1
    const isFirst = segmentsList?.length > 1 && index >= 0 && index < segmentsList?.length - 1
    const isTripStart = index === 0
    const isLast = index <= segmentsList?.length - 1 && segmentsList?.length > 1 && index > 0

    const getLayoverDurationFromInfo = (arrival: any) => {
        const airportCode = arrival?.airport?.code
        const match = (Array.isArray(layoverInfo) ? layoverInfo : []).find(
            (layover: any) => layover?.airport === airportCode
        )
        const { hh = 0, mm = 0 } = match?.totalDuration || {}
        return { hh, mm, totalMinutes: hh * 60 + mm }
    }

    //Operating Airline Info
    const shouldShowOperatingAirline = !isEmpty(operatingAirlineCode) && operatingAirlineCode !== airlineCode
    const operatingAirlineName = airlineDetail?.[operatingAirlineCode]?.name || ''

    // Airport change detection logic (single callout per journey)
    const getAirportChangeCallout = () => {
        const { from, to } = searchQuery
        // Use entire journey's first and last segments (not the technical layover split of a single leg)
        const journeyFirstSegment = segmentsList?.[0] || {}
        const journeyLastSegment = segmentsList?.[segmentsList.length - 1] || {}
        const firstDepartureCode = journeyFirstSegment?.departure?.airport?.code
        const lastArrivalCode = journeyLastSegment?.arrival?.airport?.code
        const isOnwardLeg = firstDepartureCode && firstDepartureCode === from
        if(!isOnwardLeg) {
            if(firstDepartureCode === to) return null
            return {
                actualAirportCode: firstDepartureCode,
                actualAirportName: airportDetail[firstDepartureCode].name,
                actualCityName: airportDetail[firstDepartureCode].city,
                searchedAirportCode: to,
                journeyType: 'departure' as const
            }
        }
        // Onward (J1): show only if final arrival differs from searched destination (to)
        if (journeyIndex === 0) {
            // Render only once for the journey (at the first FlightItinerary block)
            if (index !== 0) return null
            if (to && lastArrivalCode && lastArrivalCode !== to) {
                const airportData = airportDetail[lastArrivalCode]
                if (airportData) {
                    return {
                        actualAirportCode: lastArrivalCode,
                        actualAirportName: airportData.name,
                        actualCityName: airportData.city,
                        searchedAirportCode: to,
                        journeyType: 'arrival' as const
                    }
                }
            }
            return null
        }

        // Return (J2): show only if first departure differs from searched destination (to)
        if (journeyIndex > 0) {
            // Render only once for the journey (at the first FlightItinerary block)
            if (index !== 0) return null
            if (to && firstDepartureCode && firstDepartureCode !== to) {
                const airportData = airportDetail[firstDepartureCode]
                if (airportData) {
                    return {
                        actualAirportCode: firstDepartureCode,
                        actualAirportName: airportData.name,
                        actualCityName: airportData.city,
                        searchedAirportCode: to,
                        journeyType: 'departure' as const
                    }
                }
            }
            return null
        }
        return null
    }

    const changeCallout = getAirportChangeCallout()
    const checkTransitionVisaRequired = (arrival: any) => getTransitVisaRequired(arrival, layoverInfo)

    return (
        <>
            {index === 0 && changeCallout && (
                <AirportChangeNotification
                    actualAirportCode={changeCallout?.actualAirportCode}
                    actualAirportName={changeCallout?.actualAirportName}
                    actualCityName={changeCallout?.actualCityName}
                    journeyType={changeCallout?.journeyType}
                    searchedAirportCode={changeCallout?.searchedAirportCode}
                />
            )}
            
            {segmentsWithTechnicalLayover.map((layoverSegment: any, segmentIndex: number) => {
                const {
                    airlineCode = '',
                    arrival = {},
                    departure = {},
                    fltNo = '',
                    duration = {}
                } = layoverSegment || {}
                const { airport: arrivalAirport } = arrival || {}
                const { time: arrTime = '' } = arrivalAirport || {}
                const { name: airlineName = '' } = airlineDetail[airlineCode] || {}
                const { airport: departAirport = {} } = departure || {}
                const isFirstSplitLeg = segmentIndex === 0
                const isLastSplitLeg = segmentIndex === segmentsWithTechnicalLayover.length - 1
                const splitIsTripStart = isTripStart && isFirstSplitLeg
                const splitIsTripEnd = isTripEnd && isLastSplitLeg
                const splitIsLast = !isFirstSplitLeg || isLast
                const splitIsFirst = !isLastSplitLeg || isFirst
                
                return (
                    <>
                        <Container className="ml-12">
                            <ArrivalDepartInfo
                                airport={departAirport}
                                airportDetail={airportDetail}
                                isLast={splitIsLast}
                                isTripStart={splitIsTripStart}
                                type={'departure'}
                            />
                            <FlightRouteInfo
                                airlineCode={airlineCode}
                                airlineName={airlineName}
                                duration={duration}
                                fltNo={fltNo}
                                isIntl={isIntl}
                                operatingAirlineName={operatingAirlineName}
                                shouldShowOperatingAirline={shouldShowOperatingAirline}
                            />
                            <ArrivalDepartInfo
                                airport={arrivalAirport}
                                airportDetail={airportDetail}
                                isFirst={splitIsFirst}
                                isTripEnd={splitIsTripEnd}
                                type={'arrival'}
                            />
                        </Container>
                        {segmentsWithTechnicalLayover?.length - 1 > segmentIndex && (
                            <Layover
                                changeInfo={getChangeInfo(
                                    arrival,
                                    segmentsWithTechnicalLayover?.[segmentIndex + 1]?.departure,
                                    airportDetail
                                )}
                                duration={getLayoverDurationFromInfo(arrival)}
                                index={segmentIndex}
                                isIntl={isIntl}
                                isTechnicalLayover={true}
                                length={segmentsWithTechnicalLayover.length}
                                transitVisaRequired={checkTransitionVisaRequired(arrival)}
                            />
                        )}
                    </>
                )
            })}
            {segmentsList?.length - 1 > index && (
                <Layover
                    changeInfo={getChangeInfo(
                        segment?.arrival,
                        segmentsList?.[index + 1]?.departure,
                        airportDetail
                    )}
                    duration={getLayoverDurationFromInfo(segment?.arrival)}
                    index={index}
                    isIntl={isIntl}
                    length={segmentsList?.length}
                    transitVisaRequired={checkTransitionVisaRequired(segment?.arrival)}
                />

            )}
            {isIntl && index === segmentsList?.length - 1 && (
                <Container className="mt-4 pb-10">
                    <BaggageInfo baggageData={baggageAllowancesInfo} />
                </Container>
            )}
        </>
    )
}

export default FlightItinerary
